import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  studioActions,
  sel,
  PRESETS,
  formatDate,
} from '../../store/studioStore';
import { pageToCanvas } from '../../utils/canvasUtils';
import { useFileUpload } from '../../hooks/useFileUpload';

// ── Shared Modal wrapper ─────────────────────────────────────────────────────
function Modal({
  open,
  onClose,
  title,
  maxWidth = 960,
  children,
  headerExtra,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="sp-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sp-modal-card" style={{ maxWidth }}>
        <div className="sp-modal-head">
          <strong>{title}</strong>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {headerExtra}
            <button className="sp-btn" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>
        <div className="sp-modal-body">{children}</div>
      </div>
    </div>
  );
}

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  maxWidth: PropTypes.number,
  children: PropTypes.node.isRequired,
  headerExtra: PropTypes.node,
};

// ── New Document ─────────────────────────────────────────────────────────────
export function NewDocModal({ open, onClose, scheduleSave }) {
  const dispatch = useDispatch();
  const [active, setActive] = useState('A4');
  const [w, setW] = useState(210);
  const [h, setH] = useState(297);

  return (
    <Modal open={open} onClose={onClose} title="Nouveau document">
      <div className="sp-preset-grid">
        {Object.entries(PRESETS).map(([name, v]) => (
          <button
            key={name}
            className={`sp-preset${active === name ? ' active' : ''}`}
            onClick={() => {
              setActive(name);
              setW(Math.round(v.w));
              setH(Math.round(v.h));
            }}
          >
            <strong>{name}</strong>
            <div className="sp-tiny">
              {Math.round(v.w)} × {Math.round(v.h)} mm
            </div>
          </button>
        ))}
      </div>
      <div className="sp-row">
        <div>
          <label>Largeur (mm)</label>
          <input
            className="sp-field"
            type="number"
            value={w}
            onChange={(e) => setW(Number(e.target.value))}
          />
        </div>
        <div>
          <label>Hauteur (mm)</label>
          <input
            className="sp-field"
            type="number"
            value={h}
            onChange={(e) => setH(Number(e.target.value))}
          />
        </div>
      </div>
      <div
        style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}
      >
        <button
          className="sp-btn sp-btn--primary"
          onClick={() => {
            dispatch(studioActions.newDocument({ mmW: w, mmH: h }));
            dispatch(studioActions.pushHistory());
            scheduleSave();
            onClose();
          }}
        >
          Créer
        </button>
      </div>
    </Modal>
  );
}

NewDocModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  scheduleSave: PropTypes.func.isRequired,
};

// ── Preview ──────────────────────────────────────────────────────────────────
export function PreviewModal({
  open,
  onClose,
  onExportPdf,
  onExportPdfWithoutWhiteSpace,
}) {
  const pages = useSelector(sel.pages);
  const doc = useSelector(sel.doc);
  const db = useSelector(sel.db);
  const showCropMarks = useSelector(sel.showCropMarks);
  const currentPageIdx = useSelector(sel.currentPageIdx);

  const [pageIdx, setPageIdx] = useState(0);
  const [dataUrl, setDataUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setPageIdx(currentPageIdx);
  }, [open, currentPageIdx]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setDataUrl(null);
    pageToCanvas(pages[pageIdx], doc, db, showCropMarks, 2)
      .then((c) => {
        setDataUrl(c.toDataURL());
      })
      .finally(() => setLoading(false));
  }, [open, pageIdx, pages, doc, db, showCropMarks]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Aperçu"
      maxWidth={1000}
      headerExtra={
        <>
          <button
            className="sp-btn"
            disabled={pageIdx === 0}
            onClick={() => setPageIdx((p) => p - 1)}
          >
            ← Précédent
          </button>
          <span className="sp-status">
            Page {pageIdx + 1} / {pages.length}
          </span>
          <button
            className="sp-btn"
            disabled={pageIdx === pages.length - 1}
            onClick={() => setPageIdx((p) => p + 1)}
          >
            Suivant →
          </button>
          <button className="sp-btn sp-btn--accent" onClick={onExportPdf}>
            Export Per Print
          </button>
          <button
            className="sp-btn sp-btn--accent"
            onClick={onExportPdfWithoutWhiteSpace}
          >
            Export Per Share
          </button>
        </>
      }
    >
      <div className="sp-preview-wrap">
        {loading && (
          <div className="sp-pdf-status">
            <div className="sp-spinner" />
            <div>Rendu…</div>
          </div>
        )}
        {dataUrl && (
          <div style={{ padding: '20px', backgroundColor: '#ffffff' }}>
            <img
              src={dataUrl}
              style={{
                boxShadow: 'var(--sp-shadow)',
                maxWidth: '100%',
                maxHeight: '72vh',
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

PreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExportPdf: PropTypes.func.isRequired,
  onExportPdfWithoutWhiteSpace: PropTypes.func.isRequired,
};

// ── Background ───────────────────────────────────────────────────────────────
export function BackgroundModal({ open, onClose, onImport, scheduleSave }) {
  const dispatch = useDispatch();
  const currentPage = useSelector(sel.currentPage);
  const bg = currentPage?.background || {};

  const update = (changes) => {
    dispatch(studioActions.updateBackground({ ...bg, ...changes }));
    scheduleSave();
  };

  const bgInputRef = useRef(null);
  const { uploadFile } = useFileUpload();

  const handleBackgroundUpload = async (file) => {
    if (!file) return;

    try {
      const fileUrl = await uploadFile(file);

      dispatch(
        studioActions.updateBackground({
          ...bg,
          src: fileUrl,
          type: file.type.includes('pdf') ? 'pdf' : 'image',
          opacity: 1,
          scale: 100,
          mode: 'contain',
          visible: true,
        })
      );

      scheduleSave();
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Background" maxWidth={400}>
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', marginBottom: 8 }}>
          Importer une image / PDF
        </label>
        <button
          className="sp-btn sp-full"
          onClick={() => bgInputRef.current?.click()}
          style={{ marginBottom: 8 }}
        >
          Choisir un fichier
        </button>
        <input
          type="file"
          ref={bgInputRef}
          accept="image/*,application/pdf"
          onChange={(e) => handleBackgroundUpload(e.target.files?.[0])}
          style={{ display: 'none' }}
        />
        {bg?.src && (
          <button
            className="sp-btn sp-btn--danger sp-full"
            onClick={() => {
              dispatch(studioActions.updateBackground(null));
              scheduleSave();
            }}
          >
            Supprimer le background
          </button>
        )}
      </div>
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 6,
          }}
        >
          <label>Opacité</label>
          <span className="sp-tiny">
            {Math.round((bg.opacity ?? 1) * 100)}%
          </span>
        </div>
        <input
          type="range"
          className="sp-range-full"
          min="0"
          max="1"
          step="0.01"
          value={bg.opacity ?? 1}
          onChange={(e) => update({ opacity: Number(e.target.value) })}
        />
      </div>
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 6,
          }}
        >
          <label>Scale</label>
          <span className="sp-tiny">{bg.scale ?? 100}%</span>
        </div>
        <input
          type="range"
          className="sp-range-full"
          min="1"
          max="300"
          step="1"
          value={bg.scale ?? 100}
          onChange={(e) => update({ scale: Number(e.target.value) })}
        />
      </div>
      <div>
        <label style={{ display: 'block', marginBottom: 6 }}>Mode</label>
        <select
          className="sp-field sp-full"
          value={bg.mode || 'contain'}
          onChange={(e) => update({ mode: e.target.value })}
        >
          <option value="contain">Contain</option>
          <option value="cover">Cover</option>
          <option value="stretch">Stretch</option>
        </select>
      </div>
    </Modal>
  );
}

BackgroundModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
  scheduleSave: PropTypes.func.isRequired,
};

// ── PDF Page Picker ───────────────────────────────────────────────────────────
export function PdfPickerModal({ open, onClose, pdfDoc, fileName, onSelect }) {
  const [thumbs, setThumbs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !pdfDoc) return;
    setLoading(true);
    setThumbs([]);
    const renderThumbs = async () => {
      const results = [];
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        const vp = page.getViewport({ scale: 0.3 });
        const c = document.createElement('canvas');
        c.width = vp.width;
        c.height = vp.height;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, c.width, c.height);
        await page.render({ canvasContext: ctx, viewport: vp }).promise;
        results.push({ index: i, dataUrl: c.toDataURL('image/jpeg', 0.7) });
      }
      setThumbs(results);
      setLoading(false);
    };
    renderThumbs();
  }, [open, pdfDoc]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`PDF : ${fileName}`}
      maxWidth={700}
    >
      {loading && (
        <div className="sp-pdf-status">
          <div className="sp-spinner" />
          <div>Chargement des pages…</div>
        </div>
      )}
      {!loading && (
        <div className="sp-pdf-thumb-grid">
          {thumbs.map((t) => (
            <div
              key={t.index}
              className="sp-pdf-thumb"
              onClick={() => onSelect(t.index)}
            >
              <img
                src={t.dataUrl}
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 6,
                  display: 'block',
                }}
              />
              <div className="sp-pdf-page-num">Page {t.index}</div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
PdfPickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  pdfDoc: PropTypes.shape({
    numPages: PropTypes.number,
    getPage: PropTypes.func,
  }),
  fileName: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};
// ── Database ─────────────────────────────────────────────────────────────────
export function DatabaseModal({ open, onClose, scheduleSave }) {
  const dispatch = useDispatch();
  const db = useSelector(sel.db);
  const fileRef = useRef();

  const importCsv = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const lines = reader.result.split(/\r?\n/).filter((l) => l.trim());
      if (!lines.length) return;
      const sep = lines[0].includes(';') ? ';' : ',';
      const parse = (line) =>
        line.split(sep).map((v) => v.trim().replace(/^"|"$/g, ''));
      const fields = parse(lines[0]);
      const records = lines.slice(1).map((line) => {
        const vals = parse(line);
        const rec = {};
        fields.forEach((f, i) => {
          rec[f] = vals[i] ?? '';
        });
        return rec;
      });
      dispatch(studioActions.dbImportCsv({ fields, records }));
      scheduleSave();
    };
    reader.readAsText(file);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Base de données"
      maxWidth={860}
      headerExtra={
        <>
          <button
            className="sp-btn"
            onClick={() => {
              const n = prompt('Nom du champ', `Champ ${db.fields.length + 1}`);
              if (n) {
                dispatch(studioActions.dbAddField(n.trim()));
                scheduleSave();
              }
            }}
          >
            + Champ
          </button>
          <button className="sp-btn" onClick={() => fileRef.current.click()}>
            Import CSV
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            hidden
            onChange={(e) => {
              if (e.target.files[0]) importCsv(e.target.files[0]);
              e.target.value = '';
            }}
          />
        </>
      }
    >
      {db.fields.length === 0 ? (
        <div className="sp-muted" style={{ padding: '16px 0' }}>
          Aucun champ. Cliquez sur &quot;+ Champ&quot; pour commencer.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="sp-db-table">
            <thead>
              <tr>
                <th style={{ width: 36, color: 'var(--sp-muted)' }}>#</th>
                {db.fields.map((f) => (
                  <th key={f}>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      <span
                        contentEditable
                        suppressContentEditableWarning
                        style={{ outline: 'none', flex: 1, minWidth: 60 }}
                        onBlur={(e) => {
                          dispatch(
                            studioActions.dbRenameField({
                              oldName: f,
                              newName: e.target.textContent.trim(),
                            })
                          );
                          scheduleSave();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.target.blur();
                          }
                        }}
                      >
                        {f}
                      </span>
                      <button
                        className="sp-icon-btn"
                        style={{ fontSize: 10 }}
                        onClick={() => {
                          if (confirm(`Supprimer le champ "${f}" ?`)) {
                            dispatch(studioActions.dbRemoveField(f));
                            scheduleSave();
                          }
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </th>
                ))}
                <th style={{ width: 28 }} />
              </tr>
            </thead>
            <tbody>
              {db.records.map((rec, ri) => (
                <tr key={ri}>
                  <td
                    style={{
                      color: 'var(--sp-muted)',
                      fontSize: 11,
                      textAlign: 'center',
                    }}
                  >
                    {ri + 1}
                  </td>
                  {db.fields.map((f) => (
                    <td key={f}>
                      <input
                        type="text"
                        defaultValue={rec[f] ?? ''}
                        onBlur={(e) => {
                          dispatch(
                            studioActions.dbUpdateCell({
                              row: ri,
                              field: f,
                              value: e.target.value,
                            })
                          );
                          scheduleSave();
                        }}
                      />
                    </td>
                  ))}
                  <td>
                    <button
                      className="sp-icon-btn"
                      style={{ fontSize: 10 }}
                      onClick={() => {
                        dispatch(studioActions.dbRemoveRecord(ri));
                        scheduleSave();
                      }}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button
        className="sp-db-add-row"
        onClick={() => {
          dispatch(studioActions.dbAddRecord());
          scheduleSave();
        }}
      >
        + Ajouter un enregistrement
      </button>
      <div className="sp-muted" style={{ marginTop: 12 }}>
        Liez un champ à un texte via le panel <strong>Propriétés</strong>.
      </div>
    </Modal>
  );
}
DatabaseModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  scheduleSave: PropTypes.func.isRequired,
  showAlert: PropTypes.func,
};
// ── Documents list ────────────────────────────────────────────────────────────
export function DocumentsModal({
  open,
  onClose,
  api,
  onLoad,
  onNewDoc,
  showAlert,
}) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const documentId = useSelector(sel.documentId);

  useEffect(() => {
    if (!open || !api) return;
    setLoading(true);
    setError(null);
    api
      .getDocuments()
      .then(setDocs)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, api]);

  const handleDelete = async (doc) => {
    const doDelete = () => {
      api
        .deleteDocument(doc.id)
        .then(() => {
          setDocs((d) => d.filter((x) => x.id !== doc.id));
        })
        .catch((e) => {
          if (showAlert) {
            showAlert({
              title: 'Erreur',
              message: 'Erreur : ' + e.message,
              type: 'error',
            });
          } else {
            alert('Erreur : ' + e.message);
          }
        });
    };

    if (showAlert) {
      showAlert({
        title: 'Supprimer le document',
        message: `Supprimer "${doc.name}" ?`,
        type: 'warning',
        confirmText: 'Supprimer',
        onConfirm: doDelete,
      });
    } else {
      if (confirm(`Supprimer "${doc.name}" ?`)) {
        doDelete();
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Documents"
      maxWidth={600}
      headerExtra={
        <button
          className="sp-btn sp-btn--primary"
          onClick={() => {
            onClose();
            onNewDoc();
          }}
        >
          + Nouveau document
        </button>
      }
    >
      {!api && (
        <div className="sp-muted" style={{ padding: 20 }}>
          API non configurée. Fournir la prop <code>token</code> et{' '}
          <code>apiBaseUrl</code>.
        </div>
      )}
      {loading && (
        <div className="sp-pdf-status">
          <div className="sp-spinner" />
          <div>Chargement…</div>
        </div>
      )}
      {error && (
        <div style={{ color: 'var(--sp-danger)', padding: '12px 0' }}>
          Erreur : {error}
        </div>
      )}
      {!loading && !error && docs.length === 0 && (
        <div
          className="sp-muted"
          style={{ padding: '20px 0', textAlign: 'center' }}
        >
          Aucun document.
        </div>
      )}
      {!loading && !error && docs.length > 0 && (
        <div className="sp-doc-list">
          {docs.map((doc) => (
            <div key={doc.id} className="sp-doc-item">
              <div style={{ fontSize: 28 }}>📄</div>
              <div className="sp-doc-item-info">
                <div className="sp-doc-item-name">
                  {doc.name}
                  {doc.id === documentId && (
                    <span style={{ color: 'var(--sp-orange)', fontSize: 11 }}>
                      {' '}
                      (ouvert)
                    </span>
                  )}
                </div>
                <div className="sp-doc-item-meta">
                  {doc.forked_from ? '🔀 Fork · ' : ''} Modifié{' '}
                  {formatDate(doc.updated_at)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {doc.id !== documentId && (
                  <button
                    className="sp-btn"
                    onClick={() => {
                      onLoad(doc.id);
                      onClose();
                    }}
                  >
                    Ouvrir
                  </button>
                )}
                <button
                  className="sp-btn sp-btn--danger"
                  style={{ fontSize: 12 }}
                  onClick={() => handleDelete(doc)}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
DocumentsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  api: PropTypes.shape({
    getDocuments: PropTypes.func,
    deleteDocument: PropTypes.func,
  }),
  onLoad: PropTypes.func.isRequired,
  onNewDoc: PropTypes.func.isRequired,
  showAlert: PropTypes.func,
};
// ── Fork page ─────────────────────────────────────────────────────────────────
export function ForkModal({
  open,
  onClose,
  page,
  api,
  documentId,
  documentName,
  showAlert,
}) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && page) {
      const docName = documentName || 'Nouveau document';
      setName(`${docName} – ${page.id} (fork)`);
    }
  }, [open, page, documentName]);

  const confirm = async () => {
    if (!name.trim()) return;
    if (!documentId) {
      if (showAlert) {
        showAlert({
          title: 'Attention',
          message: "Sauvegardez d'abord le document.",
          type: 'warning',
        });
      } else {
        alert("Sauvegardez d'abord le document.");
      }
      return;
    }
    if (!api) {
      if (showAlert) {
        showAlert({
          title: 'Erreur',
          message: 'API non configurée.',
          type: 'error',
        });
      } else {
        alert('API non configurée.');
      }
      return;
    }
    setLoading(true);
    try {
      const forked = await api.forkPage(documentId, page.id, name.trim());
      if (showAlert) {
        showAlert({
          title: 'Succès',
          message: `✓ Fork créé : "${forked.name}"\nID : ${forked.id}`,
          type: 'success',
        });
      } else {
        alert(`✓ Fork créé : "${forked.name}"\nID : ${forked.id}`);
      }
      onClose();
    } catch (e) {
      if (showAlert) {
        showAlert({
          title: 'Erreur',
          message: 'Erreur fork : ' + e.message,
          type: 'error',
        });
      } else {
        alert('Erreur fork : ' + e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Forker la page" maxWidth={420}>
      <div className="sp-muted" style={{ marginBottom: 14 }}>
        Un nouveau document indépendant sera créé à partir de cette page. Chaque
        utilisateur pourra y travailler sans affecter l&apos;original.
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>
          Nom du nouveau document
        </label>
        <input
          className="sp-field sp-full"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && confirm()}
          placeholder="ex: Page 1 – Version Marie"
        />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="sp-btn" onClick={onClose}>
          Annuler
        </button>
        <button
          className="sp-btn sp-btn--primary"
          onClick={confirm}
          disabled={loading}
        >
          {loading ? 'Création…' : 'Créer le fork'}
        </button>
      </div>
    </Modal>
  );
}

ForkModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  page: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  api: PropTypes.shape({
    forkPage: PropTypes.func,
  }),
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  documentName: PropTypes.string,
  showAlert: PropTypes.func,
};
